import {
  CalendarMonth as CalendarIcon,
  Check as CheckIcon,
  NextWeek as NextWeekIcon,
  PhoneMissed as ContactAttemptIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { addDays, addMinutes, format } from "date-fns";
import { sv } from "date-fns/locale";
import React, { useState } from "react";
import { useUpdateNextActionMutation } from "../../../store/services/nextActionService";
import {
  useGetCalendarEventByIdQuery,
  useUpdateCalendarEventMutation,
} from "../../../store/ms-services/calendarApi";
import { WorkItemList } from "../../../store/services/timelineApiRTK";
import { FreeSlotChips } from "../../free-slots";

interface RescheduleCardProps {
  activity: WorkItemList;
}

const RescheduleCard: React.FC<RescheduleCardProps> = ({ activity }) => {
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [registerContactAttempt, setRegisterContactAttempt] = useState(false);

  const [updateWorkItem] = useUpdateNextActionMutation();
  const [updateCalendarEvent] = useUpdateCalendarEventMutation();

  const hasExternalId = Boolean(activity.ExternalId);
  const { data: calendarEvent } = useGetCalendarEventByIdQuery(
    activity.ExternalId || "",
    { skip: !hasExternalId }
  );

  const currentDate = activity.ScheduledTo ? new Date(activity.ScheduledTo) : new Date();
  const hasChanged = pendingDate !== null;

  const handleSelectDate = (date: Date) => {
    setPendingDate(date);
    setShowDatePicker(false);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!pendingDate) return;
    setSaving(true);
    setSaved(false);
    try {
      if (activity.ExternalId && calendarEvent) {
        try {
          await updateCalendarEvent({
            eventData: {
              start: { dateTime: pendingDate.toISOString(), timeZone: "utc" },
              end: {
                dateTime: addMinutes(pendingDate, calendarEvent.duration || 60).toISOString(),
                timeZone: "utc",
              },
            },
            eventId: activity.ExternalId,
          }).unwrap();
        } catch (err) {
          console.warn("Could not update calendar event:", err);
        }
      }

      await updateWorkItem({
        customerNo: activity.Customer.Id,
        request: {
          scheduledDate: format(pendingDate, "yyyy-MM-dd'T'HH:mm:ss"),
          title: activity.Name,
          description: activity.Description,
          teamId: activity.Team?.Id,
          assignedToId: activity.AssignedTo?.Id,
          externalId: activity.ExternalId,
          workNo: activity.WorkId ?? undefined,
          type: activity.Type,
          workItemNo: activity.Id,
          RegisterContactAttempt: registerContactAttempt,
        },
      }).unwrap();

      setSaved(true);
      setPendingDate(null);
      setRegisterContactAttempt(false);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Error rescheduling:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingDate(null);
    setShowDatePicker(false);
    setRegisterContactAttempt(false);
  };

  const displayDate = pendingDate ?? currentDate;

  const suggestions = [
    {
      label: "+1v",
      date: addDays(currentDate, 7),
      icon: <NextWeekIcon sx={{ fontSize: 14 }} />,
    },
    {
      label: "+2v",
      date: addDays(currentDate, 14),
      icon: <CalendarIcon sx={{ fontSize: 14 }} />,
    },
  ];

  const formatDateStr = (date: Date) => {
    try {
      return format(date, "EEE d MMM 'kl' HH:mm", { locale: sv });
    } catch {
      return "Ej schemalagd";
    }
  };

  return (
    <Box>
      {/* Date + quick actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <ScheduleIcon sx={{ fontSize: 16, color: hasChanged ? "primary.main" : "text.disabled" }} />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: hasChanged ? "primary.main" : "text.primary" }}
        >
          {formatDateStr(displayDate)}
        </Typography>

        {saved && <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />}

        <Box sx={{ ml: "auto", display: "flex", gap: 0.5, alignItems: "center" }}>
          {suggestions.map((s) => (
            <Chip
              key={s.label}
              label={s.label}
              icon={s.icon}
              size="small"
              variant="outlined"
              clickable
              disabled={saving}
              onClick={() => handleSelectDate(s.date)}
              sx={{
                height: 24,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { ml: 0.5 },
                "&:hover": {
                  bgcolor: alpha("#1976d2", 0.08),
                  borderColor: "primary.main",
                },
              }}
            />
          ))}
          <Chip
            label="Datum..."
            size="small"
            variant={showDatePicker ? "filled" : "outlined"}
            color={showDatePicker ? "primary" : "default"}
            clickable
            disabled={saving}
            onClick={() => setShowDatePicker(!showDatePicker)}
            sx={{ height: 24, fontSize: "0.7rem" }}
          />
        </Box>
      </Box>

      {/* Expandable datepicker */}
      <Collapse in={showDatePicker}>
        <Box sx={{ mt: 1.5 }}>
          <DateTimePicker
            value={displayDate}
            onChange={(date) => {
              if (date) handleSelectDate(date);
            }}
            minDateTime={new Date()}
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
          {/* Villkorad rendering (inte bara Collapse) så att kollapsade kort
              inte gör några kalenderanrop */}
          {showDatePicker && (
            <FreeSlotChips
              dense
              maxSlots={3}
              date={displayDate}
              durationMinutes={calendarEvent?.duration || 60}
              selectedTime={pendingDate}
              excludeCalendarEventId={activity.ExternalId || undefined}
              excludeWorkItemId={activity.Id}
              disabled={saving}
              onSelectSlot={handleSelectDate}
            />
          )}
        </Box>
      </Collapse>

      {/* Save/cancel — only visible when changed */}
      <Collapse in={hasChanged && !saving}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: 1,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={registerContactAttempt}
                onChange={(e) => setRegisterContactAttempt(e.target.checked)}
                sx={{
                  color: "#9B6B00",
                  "&.Mui-checked": { color: "#9B6B00" },
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ContactAttemptIcon sx={{ fontSize: 15, color: "#9B6B00" }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Kunden nåddes ej
                </Typography>
              </Box>
            }
            sx={{ m: 0 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={handleCancel} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
              Ångra
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 1.5, px: 2 }}
            >
              Flytta hit
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Saving state */}
      {saving && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, justifyContent: "flex-end" }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Flyttar...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default RescheduleCard;
